import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useSubmit, useActionData } from '@remix-run/react';
import type { User } from '~/services/api/types/user.types';

// Kullanıcı tipi veya basitleştirilmiş kullanıcı tipi
type AuthUser = User | {
  id: string;
  email: string;
  fullName: string;
  roles: string[];
};

type AuthContextType = {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children, initialUser }: { children: React.ReactNode, initialUser?: AuthUser | null }) {
  const [user, setUser] = useState<AuthUser | null>(initialUser || null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const submit = useSubmit();
  const actionData = useActionData() as { user?: AuthUser } | undefined;

  // Action data değiştiğinde kullanıcı bilgisini güncelle
  useEffect(() => {
    if (actionData?.user) {
      setUser(actionData.user);
    }
  }, [actionData]);

  // Login
  const login = (email: string, password: string, rememberMe = false) => {
    setIsLoading(true);

    const formData = new FormData();
    formData.append('email', email);
    formData.append('password', password);
    formData.append('rememberMe', rememberMe.toString());
    formData.append('_action', 'login');

    submit(formData, { method: 'post' });

    setIsLoading(false);
  };

  // Logout
  const logout = () => {
    const formData = new FormData();
    formData.append('_action', 'logout');

    submit(formData, { method: 'post', action: '/logout' });

    setUser(null);

    // Sayfayı yenile (cache temizlemek için)
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
